<template>
  <div class="main-content-wrapper">
    <AppHeaderQuickShop
      v-if="mainProduct"
      :product="mainProduct"
    />

    <div class="main-content">
      <div
        v-if="pageData?.hero"
        class="app-hero"
      >
        <BlockHeroLandingpage
          v-if="pageData?.hero"
          :key="`hero-${route?.path}`"
          :image="pageData.hero.image"
          :label="pageData.hero.label"
        />
      </div>

      <AppComponentRenderer
        v-if="isArrayNotEmpty(pageData?.mainContent)"
        :data="pageData.mainContent"
        tag="main"
        class="app-main-content"
        :class="{
          'has-padding': !pageData.hasHero,
        }"
      />
    </div>
  </div>
</template>

<script setup>
definePageMeta({
    layout: 'landingpages',
});

const version = inject('storyblokVersion', 'draft');
const storyblokApi = useStoryblokApi();
const route = useRoute();
const path = computed(() => (route.path === '/' || route.path === '' ? '/home' : route.path));
const { setData: setSEOData } = useSEO();

/*
  Fetch Storyblok Data
*/
const story = ref(null);
const storyId = ref(null);
const pageData = ref(null);
provide('pageData', pageData);
const setPageData = (content) => {
    if (!content) return;

    pageData.value = {
        mainContent: content.main_content || [],
        hero: isArrayNotEmpty(content.hero) ? content.hero[0] : {},
        hasHero: isArrayNotEmpty(content.hero),
        mainProduct: content.main_product?.product || null,
        seo: content.seo || {},
    };
};

const { data: response, error } = await useAsyncData(
    path.value, // cache key for this request
    () => storyblokApi.get(
        `cdn/stories${path.value}`,
        {
            version,
        },
    ),
    {
        watch: [path],
        transform: (res) => {
            const content = res?.data?.story?.content;

            if (!content) {
                return {
                    hero: {},
                    mainContent: [],
                    mainProduct: null,
                };
            }

            story.value = res?.data?.story;
            storyId.value = res.data.story.id;

            return {
                mainContent: content.main_content || [],
                hero: isArrayNotEmpty(content.hero) ? content.hero[0] : {},
                hasHero: isArrayNotEmpty(content.hero),
                mainProduct: content.main_product?.product || null,
                seo: content.seo || {},
            };
        },
    },
);

const mainProduct = computed(() => pageData.value?.mainProduct);

watch(() => response.value, (newValue) => {
    if (newValue) {
        pageData.value = newValue;
    }
}, { immediate: true, deep: true });

/*
    Watch response and error
*/
watch(() => error.value, (newValue) => {
    if (newValue) {
        throw createError({
            statusCode: 404,
            message: 'Page not found',
            fatal: true,
        });
    }
}, { immediate: true });

watch(() => response.value, (newValue) => {
    if (newValue.seo) {
        setSEOData(newValue.seo);
    }
}, { immediate: true });

/*
  Storyblok Preview
*/
watch(() => storyId.value, (newValue) => {
    if (newValue && process.client && version === 'draft') {
        useStoryblokBridge(storyId.value, (evStory) => {
            const { content } = evStory;
            setPageData(content);
        });
    }
}, { immediate: true });
</script>

<style lang="scss" scoped>
.app-main-content {
    @include grid-layout();

    width: 100%;
    max-width: var(--grid-width);
    padding: 0 var(--page-padding);
    margin: 0 auto;

    @include fluid('margin-bottom', 80px, 120px);

    &.no-margin {
        margin: 0;
    }

    @include tablet-portrait{
        &.has-padding {
            @include fluid('padding-top', 140px, 278px);
        }
    }
}
</style>
